import type { PropsWithChildren } from 'react';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { ProductDependenciesContext } from '@atlassian/company-hub-common';
import type {
	ProductDependenciesProviderProps,
	FeedbackType,
	UseMediaConfig,
} from '@atlassian/company-hub-common';

import {
	createRendererMediaProvider,
	createEditorMediaProvider,
} from '@confluence/fabric-media-support';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { ADVANCED_SEARCH, PEOPLE_DIRECTORY } from '@confluence/named-routes';
import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider';
import {
	getMacroAttributesFromADFNode,
	getExperienceName,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker';
import { MacroExperienceStop } from '@confluence/macro-tracker/entry-points/MacroExperienceStop';
import { MacroExperienceStart } from '@confluence/macro-tracker/entry-points/MacroExperienceStart';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess';
import {
	CARDS_IMAGE_UPLOAD_EXPERIENCE,
	COMPANY_HUB_CARD_AI_DESCRIPTION_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout';
import { useSessionData } from '@confluence/session-data';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { getMonitoringClient } from '@confluence/monitoring';
import { useFeedbackCollector } from '@confluence/company-hub-utils/entry-points/FeedbackCollectorLoader';
import { ExtensionConfigUpsellLoader } from '@confluence/change-edition/entry-points/PremiumExtensionsUpsell';
import { ShortcutListener } from '@confluence/shortcuts';
import { getSavedThemeLocal } from '@confluence/theming/entry-points/getSavedThemeLocal';
import { useSearchSessionId } from '@confluence/search-session';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import { getUniquePageLoadId } from '@confluence/unique-page-load-id';
import { usePageSpaceKey } from '@confluence/page-context';

import { usePremiumGate } from '../shared-components/usePremiumGate';
import { useLinkAnalyticActions } from '../shared-components/useLinkAnalyticActions';
import { AudienceSegmentationContext } from '../AudienceSegmentationContext';

import { useSegmentationTargetGroups } from './use-segmentation-target-groups/useSegmentationTargetGroups';
const useFeedbackCollectorWrapper = ({ source, type }: { source: string; type: FeedbackType }) => {
	return useFeedbackCollector({ source, type });
};

const useSegmentationTargetGroupsWrapper = ({
	nextCursor,
	initialGroupIds = [],
}: {
	initialGroupIds?: string[];
	nextCursor?: string;
}) => {
	return useSegmentationTargetGroups({ nextCursor, initialGroupIds });
};

const ShortcutListenerWrapper = ({
	accelerator,
	listener,
}: {
	accelerator: string;
	listener: () => void;
}) => {
	return React.createElement(ShortcutListener, { accelerator, listener });
};

const ExtensionConfigUpsellLoaderWrapper = ({ feature }: { feature: string }) => {
	return React.createElement(ExtensionConfigUpsellLoader, {
		feature: feature as
			| 'premiumCardsConfig'
			| 'premiumCarouselConfig'
			| 'premiumSpotlightConfig'
			| 'premiumSearchConfig',
	});
};

const submitError = (error: Error) => {
	getMonitoringClient().submitError(error, {
		attribution: Attribution.COMPANY_HUB,
	});
	markErrorAsHandled(error);
};

const SmartCardProvider = ({ children }: PropsWithChildren) => {
	return (
		<>
			{fg('confluence_smart_card_provider_preview_panel') ? (
				children
			) : (
				<ConfluenceSmartCardProvider>{children}</ConfluenceSmartCardProvider>
			)}
		</>
	);
};

export const useConfluenceMediaUploadExperience = () => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const start = useCallback(
		({ analyticsSource }: { analyticsSource: string }) => {
			experienceTracker.start({
				name: CARDS_IMAGE_UPLOAD_EXPERIENCE,
				attributes: {
					source: analyticsSource,
				},
			});
		},
		[experienceTracker],
	);

	const succeed = useCallback(() => {
		experienceTracker.succeed({ name: CARDS_IMAGE_UPLOAD_EXPERIENCE });
	}, [experienceTracker]);

	const stopOnError = useCallback(
		({ error }: { error: Error }) => {
			experienceTracker.stopOnError({ name: CARDS_IMAGE_UPLOAD_EXPERIENCE, error });
		},
		[experienceTracker],
	);

	return {
		start,
		succeed,
		stopOnError,
	};
};

export const useConfluenceAIDescriptionExperience = () => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const start = useCallback(
		({
			analyticsSource,
			confluencePageId,
		}: {
			analyticsSource: string;
			confluencePageId: string;
		}) => {
			experienceTracker.start({
				name: COMPANY_HUB_CARD_AI_DESCRIPTION_EXPERIENCE,
				attributes: {
					source: analyticsSource,
					contentId: confluencePageId,
				},
			});
		},
		[experienceTracker],
	);

	const abort = useCallback(
		({ reason }: { reason: string }) => {
			experienceTracker.abort({
				name: COMPANY_HUB_CARD_AI_DESCRIPTION_EXPERIENCE,
				reason,
			});
		},
		[experienceTracker],
	);

	const succeed = useCallback(() => {
		experienceTracker.succeed({ name: COMPANY_HUB_CARD_AI_DESCRIPTION_EXPERIENCE });
	}, [experienceTracker]);

	const stopOnError = useCallback(
		({ error }: { error: Error }) => {
			experienceTracker.stopOnError({ name: COMPANY_HUB_CARD_AI_DESCRIPTION_EXPERIENCE, error });
		},
		[experienceTracker],
	);

	return {
		start,
		abort,
		succeed,
		stopOnError,
	};
};

export const getCoverImageCollectionAndFileId = (
	activeContentId: string,
	maybeCustomCoverImage: string,
): [string, string] => {
	if (maybeCustomCoverImage.indexOf('/') !== -1) {
		const temp = maybeCustomCoverImage.split('/');
		return [temp[0], temp[1]];
	}

	// If it's not a stored cover image, we return original contentId & imageSrc
	return [activeContentId, maybeCustomCoverImage];
};

export const useMediaConfig = ({
	contentId,
	imageSrc,
}: {
	contentId: string;
	imageSrc: string;
}): UseMediaConfig | null => {
	const [mediaConfig, setMediaConfig] = useState<UseMediaConfig | null>(null);

	// When imageSrc is a stored cover image, it comes in format '{contentId}/{fileId}'
	// We need to split the string to get the collectionId and fileId
	const [collectionId, fileId] = getCoverImageCollectionAndFileId(contentId, imageSrc);

	useEffect(() => {
		async function createMediaConfig() {
			if (!createRendererMediaProvider) {
				return;
			}

			const { viewMediaClientConfig } = await createRendererMediaProvider({
				contentId: collectionId,
				isExternalShare: false,
			});

			if (!viewMediaClientConfig) {
				setMediaConfig(null);
				return;
			}

			setMediaConfig({
				collection: `contentId-${collectionId}`,
				authProvider: viewMediaClientConfig.authProvider,
				fileId,
			});
		}

		void createMediaConfig();
	}, [collectionId, fileId]);

	return mediaConfig;
};

export const ConfluenceDependenciesProviderComponent = ({
	mode,
	extensionNode,
	contentId = '',
	children,
}: ProductDependenciesProviderProps) => {
	const mediaUploadExperienceTracker = useConfluenceMediaUploadExperience();
	const aiDescriptionExperienceTracker = useConfluenceAIDescriptionExperience();
	const sessionData = useSessionData();
	const { cloudId, displayName, isAdminHubAIEnabled, orgId, userId } = sessionData || {};
	const [{ searchSessionId }] = useSearchSessionId();
	const [spaceKey] = usePageSpaceKey();
	const { isTargetAudience } = useContext(AudienceSegmentationContext);

	const shouldShowPremiumUpsell = usePremiumGate();
	const ConfigUpsellScreen = useMemo(
		() =>
			shouldShowPremiumUpsell
				? ({ feature }: { feature: string }) => (
						<ExtensionConfigUpsellLoaderWrapper feature={feature} />
					)
				: undefined,
		[shouldShowPremiumUpsell],
	);

	const experienceStartComponent = useCallback(() => {
		if (!extensionNode) return null;
		const macroAttributes = getMacroAttributesFromADFNode(extensionNode);
		const experienceName = getExperienceName(mode, extensionNode);

		return (
			<MacroExperienceStart
				name={experienceName}
				attributes={macroAttributes}
				extensionKey={extensionNode.extensionKey}
				contentId={contentId}
				mode={mode}
				timeout={ExperienceTimeout.MACRO_LOAD}
			/>
		);
	}, [extensionNode, contentId, mode]);

	const experienceSuccessComponent = useCallback(() => {
		if (!extensionNode) return null;
		return (
			<MacroExperienceSuccess
				name={getExperienceName(mode, extensionNode)}
				contentId={contentId}
				extensionKey={extensionNode.extensionKey}
				mode={mode}
				attributes={getMacroAttributesFromADFNode(extensionNode)}
			/>
		);
	}, [extensionNode, contentId, mode]);

	const experienceStopComponent = useCallback(
		({ error }: { error?: Error }) => {
			if (!extensionNode) return null;
			return (
				<MacroExperienceStop
					error={error}
					name={getExperienceName(mode, extensionNode)}
					contentId={contentId}
					extensionKey={extensionNode.extensionKey}
					mode={mode}
					attributes={getMacroAttributesFromADFNode(extensionNode)}
				/>
			);
		},
		[mode, extensionNode, contentId],
	);

	const isSSRProductInDarkMode = useMemo(() => {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			return getSavedThemeLocal().colorMode === 'dark';
		}
		return undefined;
	}, []);

	const errorBoundaryComponent = useMemo(
		() =>
			({ children }: { children?: React.ReactNode }) => (
				<ErrorBoundary attribution={Attribution.COMPANY_HUB}>{children}</ErrorBoundary>
			),
		[],
	);

	const errorDisplayComponent = useMemo(
		() =>
			({ error, childrenComponents }: { error: Error; childrenComponents?: React.ReactNode }) => (
				<ErrorDisplay error={error}>{childrenComponents}</ErrorDisplay>
			),
		[],
	);

	const useAnalyticsActions = useMemo(
		() => ({
			useLinkAnalyticActions,
		}),
		[],
	);

	const value = useMemo(
		() => ({
			experienceStartComponent,
			experienceSuccessComponent,
			experienceStopComponent,
			cloudId,
			searchConfigProvider: {
				peopleDirectoryUrl: PEOPLE_DIRECTORY.toUrl(),
				advancedSearchUrl: ADVANCED_SEARCH.toUrl(),
				searchSessionId: searchSessionId || '',
			},
			displayName: displayName || '',
			isAdminHubAIEnabled,
			orgId: orgId || '',
			userId: userId || '',
			errorBoundaryComponent,
			errorDisplayComponent,
			submitError,
			markErrorAsHandled,
			useMediaConfig,
			createEditorMediaProvider,
			mediaUploadExperienceTracker,
			aiDescriptionExperienceTracker,
			useFeedbackCollector: useFeedbackCollectorWrapper,
			shortcutListener: ShortcutListenerWrapper,
			ConfigUpsellScreen,
			useSegmentationTargetGroups: useSegmentationTargetGroupsWrapper,
			isSSRProductInDarkMode,
			analyticsContext: {
				pageLoadInfo: getUniquePageLoadId(),
			},
			useAnalyticsActions,
			isCompanyHub: isCompanyHubSpaceKey(spaceKey),
			isTargetAudience,
			smartCardProviderComponent: SmartCardProvider,
			showConfigPreamble: true,
			enableTargeting: true,
			enableUnifiedSearchFeatures: true,
			enableSmartCardsFields: true,
		}),
		[
			experienceSuccessComponent,
			experienceStopComponent,
			cloudId,
			displayName,
			isAdminHubAIEnabled,
			orgId,
			userId,
			searchSessionId,
			ConfigUpsellScreen,
			isSSRProductInDarkMode,
			errorBoundaryComponent,
			errorDisplayComponent,
			experienceStartComponent,
			mediaUploadExperienceTracker,
			aiDescriptionExperienceTracker,
			useAnalyticsActions,
			spaceKey,
			isTargetAudience,
		],
	);

	return (
		<ProductDependenciesContext.Provider value={value}>
			{children}
		</ProductDependenciesContext.Provider>
	);
};

export const ConfluenceDependenciesProvider = ({
	children,
	...props
}: ProductDependenciesProviderProps) => (
	<ConfluenceDependenciesProviderComponent {...props}>
		{children}
	</ConfluenceDependenciesProviderComponent>
);
